import React, { useState, useEffect, useRef } from 'react';
import DynamicIcon from './DynamicIcon';
import apibig from "../components/Apibig";

const ModalPermisosrol = ({ isOpen, onClose, selectedPermisos, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [permisosData, setPermisosData] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [mensaje, setMensaje] = useState(null);

  // Guardamos la copia inicial para comparar cambios
  const permisosIniciales = useRef([]);

  const normalizePermisosKeys = (data) => {
    return data.map(item => ({
      ...item,
      Permisos: normalizePermisosKeys(item.Permisos || item.permisos || []),
    }));
  };

  const filterPermisosRol = (data) => {
    return data.map(item => ({
      Id: item.Id,
      Titulo: item.Titulo,
      Email: item.Email,
      Permiso: item.Permiso,
      Permisos: filterPermisosRol(item.Permisos || []),
    }));
  };

  useEffect(() => {
    if (isOpen && selectedPermisos) {
      const normalized = normalizePermisosKeys(selectedPermisos);
      const permisosRol = filterPermisosRol(normalized);
      setPermisosData(permisosRol);
      permisosIniciales.current = permisosRol; // Guardar copia inicial
      setMensaje(null);
    }
  }, [isOpen, selectedPermisos]);

  useEffect(() => {
    if (!isOpen) {
      setPermisosData([]);
      setExpandedIds(new Set());
      setMensaje(null);
      permisosIniciales.current = [];
    }
  }, [isOpen]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const updatePermissionTree = (data, id, checked) => {
    return data.map(item => {
      if (item.Id === id) {
        return { ...item, Permiso: checked };
      } else {
        return {
          ...item,
          Permisos: updatePermissionTree(item.Permisos || [], id, checked)
        };
      }
    });
  };

  const onCheckboxToggle = (id, checked) => {
    const updatedData = updatePermissionTree(permisosData, id, checked);
    setPermisosData(updatedData);
  };

  // Función para comparar permisos actuales vs iniciales y extraer solo los cambiados
  const permisosCambiados = (actual, inicial) => {
    const result = [];

    const comparar = (actItems, iniItems) => {
      for (const actItem of actItems) {
        const iniItem = iniItems.find(i => i.Id === actItem.Id);
        if (!iniItem) {
          // Nuevo permiso (no existía antes)
          result.push({
            Id: actItem.Id,
            Email: actItem.Email,
            Permiso: actItem.Permiso,
          });
        } else if (actItem.Permiso !== iniItem.Permiso) {
          // Permiso modificado
          result.push({
            Id: actItem.Id,
            Email: actItem.Email,
            Permiso: actItem.Permiso,
          });
        }
        // Comparar hijos recursivamente
        if (actItem.Permisos && actItem.Permisos.length) {
          comparar(actItem.Permisos, iniItem ? iniItem.Permisos : []);
        }
      }
    };

    comparar(actual, inicial);
    return result;
  };

  const onGuardar = async () => {
    setLoading(true);
    setMensaje(null);

    const permisosActualizados = permisosCambiados(permisosData, permisosIniciales.current);

    if (permisosActualizados.length === 0) {
      setMensaje({ tipo: 'exito', texto: "No hubo cambios para guardar." });
      setLoading(false);
      return;
    }

    const autusuario = JSON.parse(sessionStorage.getItem('data'));

    const payload = {
      appid: autusuario?.appid,
      basedatos: autusuario?.basedatos,
      permisos: permisosActualizados,
    };

    try {
      await apibig.post(`/api/roles/updatepermisos`, payload);
      setMensaje({ tipo: 'exito', texto: "Permisos actualizados correctamente." });
      permisosIniciales.current = permisosData; // Actualizar copia inicial tras guardar

      if (onSave) {
        onSave(); // Avisar al padre que se guardó con éxito
      }
    } catch (err) {
      setMensaje({ tipo: 'error', texto: "Error al actualizar permisos. Intente de nuevo." });
    } finally {
      setLoading(false);
    }
  };

  const renderPermisos = (data, level = 0) => {
    return data.flatMap(item => {
      const isExpanded = expandedIds.has(item.Id);
      const hasChildren = item.Permisos && item.Permisos.length > 0;

      return [
        <tr key={item.Id} className="border-b hover:bg-gray-50">
          <td className="py-1 text-gray-700">
            <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 24}px` }}>
              <span className={`px-2 gap-2 flex font-${level === 0 ? 'semibold' : level === 1 ? 'medium' : 'normal'}`}>
                <DynamicIcon iconName="edit" color="text-green-800" size="1em" />
                {item.Titulo}
              </span>
            </div>
          </td>
          <td>
            {hasChildren && (
              <button onClick={() => toggleExpand(item.Id)} className="focus:outline-none text-sm">
                <DynamicIcon iconName={isExpanded ? "chevrondown" : "chevronright"} size="1em" />
              </button>
            )}
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              className="w-5 h-5 accent-blue-600"
              checked={item.Permiso}
              onChange={(e) => onCheckboxToggle(item.Id, e.target.checked)}
              disabled={loading}
            />
          </td>
        </tr>,
        ...(hasChildren && isExpanded ? renderPermisos(item.Permisos, level + 1) : []),
      ];
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-md shadow-2xl w-[60rem] max-w-6xl h-[80vh] flex flex-col">
        <div className="bg-sidebar-100 text-white font-semibold text-lg px-6 py-1 rounded-t-md">
          Modificar permisos
        </div>
        <div className="flex-1 overflow-auto px-2 py-2">
                    {mensaje && (
            <div
              className={`mt-2 px-4 py-2 rounded ${
                mensaje.tipo === 'exito' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {mensaje.texto}
            </div>
          )}
          <div className="border rounded-md overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead className="bg-sidebar-200 text-white sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-1">Módulo</th>
                  <th className="px-4 py-1"></th>
                  <th className="text-center px-4 py-1">Permiso</th>
                </tr>
              </thead>
              <tbody>{renderPermisos(permisosData)}</tbody>
            </table>
          </div>

        </div>
        <div className="text-center text-blue-600 text-[1rem] py-1">
          {loading && "Actualizando permisos, por favor espere..."}
        </div>
        <div className="flex justify-end gap-2 pb-3 pr-3">
          <button
            onClick={onGuardar}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={loading}
          >
            Guardar
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            disabled={loading}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalPermisosrol;
